import { MonoTypeOperatorFunction, retry, tap, timer } from 'rxjs'
import { environment } from '@appEnvironments/environment'

const transientStatuses = [0, 502, 503, 504]

/* In local development the host reports a not-yet-started service layer as a
   500 (client-before-service startup race). Once any call succeeds the service
   is up, so 500s revert to real faults and surface immediately. */
let retry500 = environment.isLocalDevelopment

/**
 * Retries transient unreachable-service failures with a short backoff:
 *   - status 0  - connection refused / network error
 *   - 502/503/504 - service restarting or unavailable behind a gateway
 *   - 500 (local development only, until the first success) - the local
 *     client-before-service startup race
 * Any other error rethrows immediately so real failures surface fast.
 *
 * Place first in the pipe, upstream of take/catchError.
 *
 * @param count       additional attempts after the initial one (default 2)
 * @param baseDelayMs backoff base; attempt n waits n * baseDelayMs (default 1500)
 */
export function retryTransient<T>(count = 2, baseDelayMs = 1500): MonoTypeOperatorFunction<T> {
   return (source) => source.pipe(
      retry({
         count,
         delay: (err, retryCount) => {
            const transient = transientStatuses.includes(err?.status)
               || (retry500 && err?.status === 500)
            if (!transient) {
               throw err
            }
            return timer(baseDelayMs * retryCount)
         }
      }),
      tap(() => retry500 = false)
   )
}
